import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { PAGE_SIZE_OPTIONS } from '../../../shared/components/ui/pretty-select/common-options';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { rsql, rsqlTextSearch } from '../../../shared/helpers/rsql';
import { PaymentMethodsService } from '../../../shared/services/payment-methods.service';
import {
  PAYMENT_METHOD_TYPE_LABEL,
  PaymentMethod,
  PaymentMethodType,
} from '../../../shared/types/payment-method.types';

const SEARCH_FIELDS = ['name', 'serviceCode'] as const;

const TYPE_OPTIONS: PrettySelectOption<PaymentMethodType | null>[] = [
  { value: 'WALLET', label: 'Wallet mobile' },
  { value: 'CARD', label: 'Carte bancaire' },
  { value: 'CASH', label: 'Espèces' },
];

@Component({
  selector: 'app-payment-methods-list',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    PageHeaderComponent,
    DataStateComponent,
    PrettySelectComponent,
  ],
  templateUrl: './payment-methods-list.component.html',
})
export class PaymentMethodsListComponent implements OnInit {
  private readonly service = inject(PaymentMethodsService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly items = signal<PaymentMethod[]>([]);
  readonly totalItems = signal(0);
  readonly totalPages = signal(0);
  readonly page = signal(1);
  readonly size = signal(10);

  readonly searchCtrl = this.fb.control('');
  readonly typeCtrl = this.fb.control<PaymentMethodType | null>(null);

  /** id de la méthode en cours de bascule (désactive son bouton). */
  readonly togglingId = signal<number | null>(null);

  readonly typeOptions = TYPE_OPTIONS;
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly typeLabel = PAYMENT_METHOD_TYPE_LABEL;

  readonly pageWindow = computed<number[]>(() => {
    const total = this.totalPages();
    if (total <= 1) return total === 1 ? [1] : [];
    const current = this.page();
    const window = 5;
    let start = Math.max(1, current - Math.floor(window / 2));
    const end = Math.min(total, start + window - 1);
    start = Math.max(1, end - window + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  });

  ngOnInit(): void {
    this.searchCtrl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.page.set(1);
        this.load();
      });

    this.typeCtrl.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.page.set(1);
        this.load();
      });

    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    const search = rsql.and(
      rsqlTextSearch(SEARCH_FIELDS, this.searchCtrl.value),
      rsql.eq('type', this.typeCtrl.value),
    );
    this.service
      .list({
        page: this.page(),
        size: this.size(),
        search: search ?? undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.items.set(res.data ?? []);
          this.totalItems.set(res.totalItems ?? 0);
          this.totalPages.set(res.totalPages ?? 0);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.items.set([]);
          this.totalItems.set(0);
          this.totalPages.set(0);
          this.error.set(toErrorMessage(err));
        },
      });
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages() || p === this.page()) return;
    this.page.set(p);
    this.load();
  }

  changeSize(s: number): void {
    if (!s || s === this.size()) return;
    this.size.set(s);
    this.page.set(1);
    this.load();
  }

  /** Active/désactive une méthode — `PUT change-status/{id}`. */
  toggleStatus(m: PaymentMethod): void {
    if (this.togglingId() !== null) return;
    this.togglingId.set(m.id);
    this.error.set(null);
    this.service
      .changeStatus(m.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.togglingId.set(null);
          this.items.update((list) =>
            list.map((x) => (x.id === m.id ? { ...x, status: updated.status } : x)),
          );
        },
        error: (err: unknown) => {
          this.togglingId.set(null);
          this.error.set(toErrorMessage(err, 'Le changement de statut a échoué.'));
        },
      });
  }
}
